import React from "react";
import { useNavigate } from "react-router-dom";
import { mediaUrl } from "@/lib/api";
import api from "@/lib/api";
import StarRating from "@/components/StarRating";
import { TID } from "@/constants/testIds";
import { ArrowSquareOut, Sparkle, ChatCircle, CursorClick, Fire } from "@phosphor-icons/react";

const FALLBACK = "https://images.unsplash.com/photo-1774271074342-93257fc7486f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const TRENDING_THRESHOLD = 15;

export default function DealCard({ link, index = 0 }) {
  const navigate = useNavigate();

  const handleVisit = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/links/${link.id}/click`);
      window.open(data.url, "_blank", "noopener");
    } catch {
      window.open(link.url, "_blank", "noopener");
    }
  };

  return (
    <div
      onClick={() => navigate(`/deal/${link.id}`)}
      data-testid={TID.dealCard}
      className={`group relative bg-[#18181B] border cursor-pointer flex flex-col transition-all duration-200 hover:-translate-y-1 animate-float-up
        ${link.is_sponsored ? "border-[#FF007F] sponsored-glow" : "border-zinc-800 hover:border-[#FF007F] hover:shadow-[6px_6px_0px_#FF007F]"}`}
      style={{ animationDelay: `${Math.min(index * 60, 500)}ms` }}
    >
      {/* Badges */}
      <div className="absolute top-3 left-3 z-10 flex flex-col gap-2 items-start">
        {link.is_sponsored && (
          <span className="flex items-center gap-1 bg-[#FF007F] text-white text-[10px] font-black uppercase tracking-widest px-2 py-1" data-testid="sponsored-badge">
            <Sparkle size={12} weight="fill" /> Sponsored
          </span>
        )}
        {link.is_mine && (
          <span className="bg-[#00FFFF] text-black text-[10px] font-black uppercase tracking-widest px-2 py-1" data-testid="your-link-badge">
            ★ Your Link
          </span>
        )}
        {link.is_featured && !link.is_sponsored && (
          <span className="bg-[#FFD600] text-black text-[10px] font-black uppercase tracking-widest px-2 py-1">
            Featured
          </span>
        )}
        {link.click_count >= TRENDING_THRESHOLD && (
          <span className="flex items-center gap-1 bg-[#39FF14] text-black text-[10px] font-black uppercase tracking-widest px-2 py-1 animate-pulse" data-testid="trending-badge">
            <Fire size={12} weight="fill" /> Trending
          </span>
        )}
      </div>

      <div className="relative overflow-hidden h-48 bg-zinc-900">
        <img
          src={mediaUrl(link.image_url) || FALLBACK}
          alt={link.title}
          onError={(e) => { e.currentTarget.src = FALLBACK; }}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>

      <div className="p-4 flex flex-col flex-1 gap-3">
        <h3 className="font-display font-bold text-base leading-tight line-clamp-2">{link.title}</h3>
        {link.description && <p className="text-sm text-zinc-400 line-clamp-2">{link.description}</p>}

        <div className="mt-auto space-y-3">
          <div className="flex items-center justify-between">
            <StarRating value={link.avg_rating} count={link.review_count} size={14} />
            <div className="flex items-center gap-3 text-xs text-zinc-500 font-mono">
              {link.click_count > 0 && <span className="flex items-center gap-1" data-testid="click-count"><CursorClick size={13} /> {link.click_count}</span>}
              <span className="flex items-center gap-1"><ChatCircle size={13} /> {link.comment_count}</span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            {link.price ? (
              <span className="font-mono font-extrabold text-lg text-[#39FF14]">{link.price}</span>
            ) : <span className="font-mono text-xs text-zinc-500 flex items-center gap-1"><CursorClick size={13}/> {link.click_count}</span>}
            <button
              onClick={handleVisit}
              data-testid={TID.dealVisitBtn}
              className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 bg-[#FF007F] text-white border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors"
            >
              Visit <ArrowSquareOut size={14} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
